import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import {
  Button, Layout, Divider, Form, Table, Alert,
  Card, Row, Col, Input, DatePicker, InputNumber, Typography,
  Spin
} from 'antd';
import {
  DeleteOutlined, ExportOutlined, PlusOutlined, SaveOutlined,
} from '@ant-design/icons';
import SubHeader from '../components/common/SubHeader';
import { defaultBOMTransactionModel, getBOMTranscationStatus } from '../constance/dataTypes';
import { URL_BOM } from '../constance/urls';
import { DELETE, GET, POST, PUT } from '../utils/HttpClient';
import dayjs from "dayjs";
import BOMModal from '../components/bom/BOMModal';
import { PATH } from '../routes/Routes';
import * as _ from 'lodash';
import ConfirmModal from '../components/common/ConfirmModal';
import { Toast } from '../utils/Toast';
import { formatComma } from '../utils/stringFormat';
import { calculateWoodSizeVolume } from '../utils/woodSize';
import { HUMAN_DATE_FORMAT } from '../constance/strings';
import ColorButton from '../components/common/ColorButton';
import { BLUE_COLOR, GREEN } from '../constance/color';
import { useWindowDimensionContext } from '../hooks/WindowDimensionProvider';

export default function BOMDetailView() {
  const params = useParams();
  const history = useHistory();
  const { t } = useTranslation();
  const { Title } = Typography;
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = React.useState(false);
  const [errorMessages, setErrorMessages] = React.useState(null);
  const [addModalOpen, setAddModalOpen] = React.useState(false);
  const [isDeleteConFirmModalOpen, setIsDeleteConFirmModalOpen] = React.useState(false);
  const [isInterfaceConFirmModalOpen, setIsInterfaceConFirmModalOpen] = React.useState(false);
  const [deleteBOMTarget, setDeleteBOMTarget] = React.useState(null);
  const isCreate = params.id === 'add';
  const [editable, setEditable] = React.useState(true);
  const { size } = useWindowDimensionContext();

  // saving for initialize and calculate form due to antd table is remove form data
  const [bomListData, setBomListData] = React.useState([])
  const bomListValue = Form.useWatch('bom_list', form)
  const hasNullWoodSize = bomListData?.some(item => item.new_wood_size === null) || false;
  const [summaryData, setSummaryData] = React.useState([]);

  const containerWidth = {
    xs: '100%',
    sm: 723,
    md: 933,
    lg: 1127,
    xl: 1127,
    xxl: 1127,
  }
  const fetchData = async () => {
    setIsLoading(true);
    setErrorMessages(null);
    try {
      const response = await GET(`${URL_BOM.BOM_TRANSACTION}${params.id}/`);
      form.setFieldsValue({
        ...defaultBOMTransactionModel,
        ...response.data,
        date: response.data.date ? dayjs(response.data.date) : null,
      });
      setEditable(!_.get(response, 'data.is_completed', true) && !_.get(response, 'data.is_interfaced', true))
      setBomListData(response.data.bom_list)
    } catch (error) {
      setErrorMessages(error.errorMessages);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (values) => {
    const summaryData = calculatedSummary();
    setErrorMessages(null);
    setIsLoading(true);
    try {
      if (isCreate) {
        await createData(values);
      } else {
        await PUT(`${URL_BOM.BOM_TRANSACTION}${params.id}/`, values)
        Toast.success(t('success'));
        fetchData();
      }
      setSummaryData(summaryData);
    } catch (error) {
      setErrorMessages(error.errorMessages);
    } finally {
      setIsLoading(false);
    }
  };

  const createData = async (values) => {
    const response = await POST(URL_BOM.BOM_TRANSACTION, values)
    Toast.success(t('success'));
    history.replace(`${PATH.BOM}${_.get(response.data, 'id')}`)
  }

  const handleDelete = async () => {
    try {
      setIsLoading(true);
      await DELETE(`${URL_BOM.BOM_TRANSACTION}${params.id}`);
      Toast.success(`${t('delete')} ${t('success')}`);
      history.replace(PATH.BOM);
    } catch (error) {
      setErrorMessages(error.errorMessages);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDoInterfaceAX = async (values) => {
    const formValidation = await form.validateFields();

    if (formValidation) {
      setErrorMessages(null);
      setIsLoading(true);
      try {
        await handleSave(values);
        await POST(`${URL_BOM.BOM_TRANSACTION}${params.id}/interface/`);
        history.replace(PATH.BOM);
      } catch (error) {
        setErrorMessages(error.errorMessages);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleDeleteBOM = async () => {
    setErrorMessages(null);
    setIsLoading(true);
    const deleteParams = deleteBOMTarget
    setDeleteBOMTarget(null);
    try {
      await POST(`${URL_BOM.BOM_TRANSACTION}${params.id}/remove-bom/`, { ...deleteParams })
      Toast.success(`${t('delete')} ${t('success')}`);
      fetchData();
    } catch (error) {
      setErrorMessages(error.errorMessages)
    } finally {
      setIsLoading(false);
    }
  }

  const calculatedSummary = () => {
    const results = [];
    (bomListData || []).forEach(bom => {
      if (!bom.new_wood_size || !bom.new_amount) return;
      let target = results.find(result =>
        result.new_wood_size === bom.new_wood_size &&
        result.pallet_quantity === bom.new_amount
      );
      if (target) {
        target.pallet += 1;
        target.total_wood = target.pallet_quantity * target.pallet;
        target.total_volume = calculateWoodSizeVolume(target.new_wood_size) * target.pallet_quantity * target.pallet;
      } else {
        results.push({
          new_wood_size: bom.new_wood_size,
          pallet: 1,
          pallet_quantity: bom.new_amount,
          total_wood: bom.new_amount,
          total_volume: calculateWoodSizeVolume(bom.new_wood_size) * bom.new_amount,
        });
      }
    });
    return results;
  };

  React.useEffect(() => {
    if (!isCreate) {
      fetchData();
    }
  }, [params.id])

  React.useLayoutEffect(() => {
    if (!addModalOpen && !isCreate) {
      fetchData();
    }
  }, [addModalOpen])

  React.useEffect(() => {
    setSummaryData(calculatedSummary(bomListValue))
  }, [bomListValue])

  const bomColumns = [
    {
      title: t('bom_transactions.boms.original_wood_size'),
      dataIndex: 'original_wood_size',
      render: (value, record, idx) => (
        <div>
          <Form.Item hidden name={['bom_list', idx, 'original_wood_size']}/>
          {value}
        </div>
      )
    },
    {
      title: t('bom_transactions.boms.new_wood_size'),
      render: (text, record, idx) => (
        <Form.Item
          style={{ margin: 0 }}
          name={['bom_list', idx, 'new_wood_size']}
          initialValue={record.new_wood_size}
          rules={[
            { required: true, message: t('error_messages.validation.required') },
            {
              validator: (_, value) => {
                if (value <= 0 && value != '') {
                  return Promise.reject(`${t('error_messages.validation.more_than')} 0`);
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input
            value={record.new_wood_size}
            maxLength="12"
            placeholder={t('bom_transactions.boms.new_wood_size')}
            onChange={(e) => (record.new_wood_size = e.target.value)}
            disabled={!editable}
          />
        </Form.Item>
      ),
    },
    {
      title: t('bom_transactions.boms.original_amount'),
      dataIndex: 'original_amount',
    },
    {
      title: t('bom_transactions.boms.new_amount'),
      render: (text, record, idx) => (
        <Form.Item
          style={{ margin: 0 }}
          name={['bom_list', idx, 'new_amount']}
          initialValue={record.new_amount}
          rules={[
            { required: true, message: t('error_messages.validation.required') },
            {
              validator: (_, value) => {
                if (value <= 0 && value != '') {
                  return Promise.reject(`${t('error_messages.validation.more_than')} 0`);
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <InputNumber
            min={0}
            value={record.new_amount}
            onChange={(value) => form.setFieldValue(`bom_list.${idx}.new_amount`, value)}
            disabled={!editable}
          />
        </Form.Item>
      ),
    },
    {
      title: t('bom_transactions.boms.original_volume'),
      render: (text, record) => formatComma(record.original_volume, 4),
    },
    {
      title: t('bom_transactions.boms.new_volume'),
      render: (text, record) =>
        formatComma(calculateWoodSizeVolume(record.new_wood_size) * record.new_amount, 4),
    },
    {
      title: t('barcodes.barcode_id'),
      dataIndex: 'barcode_no',
      render: (value, record, idx) => (
        <div>
          <Form.Item hidden name={['bom_list', idx, 'barcode']}/>
          {value}
        </div>
      )
    },
    ...(editable
      ? [
        {
          render: (text, record) => (
            <Button
              danger
              htmlType='button'
              icon={<DeleteOutlined />}
              onClick={() => setDeleteBOMTarget(record)}
            />
          ),
        },
      ]
      : []),
  ];

  const summaryColumns = [
    {
      title: t('bom_transactions.summary.wood_size'),
      dataIndex: 'new_wood_size',
    },
    {
      title: t('bom_transactions.summary.pallet'),
      dataIndex: 'pallet',
      render: (text) => formatComma(text),
    },
    {
      title: t('bom_transactions.summary.pallet_quantity'),
      dataIndex: 'pallet_quantity',
      render: (text) => formatComma(text),
    },
    {
      title: t('bom_transactions.summary.total_wood'),
      dataIndex: 'total_wood',
      render: (text) => formatComma(text),
    },
    {
      title: t('bom_transactions.summary.total_volume'),
      dataIndex: 'total_volume',
      render: (text) => formatComma(text, 4),
    },
  ];

  return (
    <div className='exportItemList-table'>
      <SubHeader />
      <div style={{ width: containerWidth[size], margin: "auto", marginTop: "1rem", }}>
        <Spin spinning={isLoading}>
          <Layout style={{ margin: '24px' }}>
            {errorMessages && (
              <Alert
                message={t('error')}
                description={errorMessages}
                type="error"
                showIcon
                style={{ marginBottom: '1rem' }}
              />
            )}

            <div style={{ display: "flex", alignItems: "center", justifyContent: 'space-between' }}>
              <Title level={2} style={{ margin: 0 }}>
                {isCreate ? t('bom_transactions.add') : t('bom_transactions.detail')}
              </Title>
              {!isCreate && editable && (
                <Button
                  type="default"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => setIsDeleteConFirmModalOpen(true)}
                >
                  {t("delete")}
                </Button>
              )}
            </div>
            <Divider />
            <Form layout="vertical" form={form} onFinish={handleSave}>
              <Card>
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item
                      label={t('bom_transactions.order_no')}
                      name="order_no"
                      rules={[{ required: true, message: t('error_messages.validation.required') }]}
                    >
                      <Input disabled={!editable} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      label={t('bom_transactions.date')}
                      name="date"
                      initialValue={dayjs().startOf('day')}
                    >
                      <DatePicker
                        style={{ width: '100%' }}
                        format={HUMAN_DATE_FORMAT}
                        disabled={!editable}
                        placeholder={t('searchs.placeholders.date')}/>
                    </Form.Item>

                  </Col>
                  <Col span={8}>
                    <Form.Item label={t('bom_transactions.status')}>
                      <Input
                        value={t(`bom_transactions.statuses.${getBOMTranscationStatus(form.getFieldValue())}`)}
                        readOnly
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item label={t('bom_transactions.description')} name="description">
                  <Input.TextArea disabled={!editable} />
                </Form.Item>

                {editable && (
                  <div style={{ textAlign: 'right' }}>
                    {!isCreate && form.getFieldValue('bom_list')?.length > 0 && (
                      <Button
                        disabled={hasNullWoodSize}
                        type="default"
                        htmlType='button'
                        icon={<ExportOutlined />}
                        style={{ marginRight: '10px' }}
                        onClick={() => setIsInterfaceConFirmModalOpen(true)}
                      >
                        {t('menus.interface_ax')}
                      </Button>
                    )}
                    <ColorButton
                      variant="solid"
                      color='primary'
                      htmlType='submit'
                      override={isCreate ? GREEN : BLUE_COLOR}
                      icon={isCreate ? <PlusOutlined /> : <SaveOutlined />}
                      loading={isLoading}
                    >
                      {isCreate ? t('add') : t('save')}
                    </ColorButton>
                  </div>
                )}

                <Divider/>

                { !isCreate && (
                  <div>
                    { editable && (
                      <ColorButton
                        style={{ float: "right" }}
                        variant="solid"
                        color='primary'
                        htmlType='button'
                        override={GREEN}
                        icon={<PlusOutlined />}
                        onClick={() => setAddModalOpen(true)}
                      >
                        {t('add')}
                      </ColorButton>
                    )}

                    <Title level={4} style={{ margin: 0, textAlign: 'left' }}>
                      {t('bom_transactions.bom')}
                    </Title>

                    <Table
                      style={{ marginTop: '1rem' }}
                      bordered
                      dataSource={bomListData}
                      rowKey="id"
                      columns={bomColumns}
                      pagination={false} />
                  </div>
                )}
              </Card>
            </Form>
            <Card style={{ marginTop: '1rem' }}>
              <Typography.Title level={4} style={{ textAlign: 'left' }}>{t('bom_transactions.summary.summary_text')}</Typography.Title>
              <Table
                dataSource={summaryData}
                bordered
                columns={summaryColumns}
                pagination={false} />
            </Card>
            <BOMModal
              open={addModalOpen}
              transaction={form.getFieldValue('id')}
              onClose={() => setAddModalOpen(false)}
            />
            <ConfirmModal
              open={isInterfaceConFirmModalOpen}
              onClose={() => setIsInterfaceConFirmModalOpen(false)}
              title={t('confirms.interface_title')}
              content={t('confirms.interface_content')}
              onConfirm={handleDoInterfaceAX}
            />
            <ConfirmModal
              open={isDeleteConFirmModalOpen}
              title={t('confirms.delete_title')}
              content={t('confirms.delete_content')}
              onConfirm={handleDelete}
              onClose={() => setIsDeleteConFirmModalOpen(false)}
            />
            <ConfirmModal
              open={!!deleteBOMTarget}
              title={t('confirms.delete_title')}
              content={t('confirms.delete_content')}
              onConfirm={handleDeleteBOM}
              onClose={() => setDeleteBOMTarget(null)}
            />
          </Layout>
        </Spin>
      </div>
    </div >
  );
}
